import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import AppStoreBadges from "@/components/landing/AppStoreBadges";
import PhoneFrame, { SCREEN_HEIGHT, SCREEN_WIDTH } from "./PhoneFrame";
import QrCard from "./QrCard";
import homeShot from "@/assets/screens/home.webp";
import askAiShot from "@/assets/screens/ask-ai.webp";
import healthShot from "@/assets/screens/health-check.webp";

// React 18 only knows the lowercase DOM attribute.
const HIGH_PRIORITY = { fetchpriority: "high" } as Record<string, string>;

/**
 * The entrance plays once. When the visitor first saw the prerendered HTML
 * (CSS already played it), React's re-render must not replay it.
 */
const PLAY_INTRO =
  typeof document === "undefined" || !document.documentElement.hasAttribute("data-prerendered");

type Side = "start" | "end";

const CALLOUTS: { key: string; side: Side; pos: string; dot: string; delay: number }[] = [
  { key: "askAi", side: "start", pos: "min-[1100px]:start-5 min-[1100px]:top-[96px]", dot: "bg-pf-teal", delay: 1.5 },
  { key: "health", side: "end", pos: "min-[1100px]:end-5 min-[1100px]:top-[128px]", dot: "bg-primary", delay: 1.75 },
  { key: "analysis", side: "start", pos: "min-[1100px]:start-0 min-[1100px]:top-[330px]", dot: "bg-pf-blue", delay: 2 },
  { key: "dividends", side: "end", pos: "min-[1100px]:end-0 min-[1100px]:top-[420px]", dot: "bg-pf-amber", delay: 2.25 },
  { key: "heatmap", side: "start", pos: "min-[1100px]:start-9 min-[1100px]:top-[548px]", dot: "bg-pf-heat", delay: 2.5 },
];

const delay = (s: number): CSSProperties => ({ animationDelay: `${s}s` });

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section
      aria-labelledby="hero-h"
      className={`overflow-clip pb-[clamp(72px,7vw,104px)] pt-[clamp(56px,6vw,84px)] text-center ${
        PLAY_INTRO ? "pf-intro" : ""
      }`}
    >
      <div className="mx-auto max-w-[980px] px-[22px]">
        <p className="pf-up mb-4 text-[13px] font-semibold uppercase tracking-[0.08em] text-primary">
          {t("home.hero.eyebrow")}
        </p>
        <h1
          id="hero-h"
          className="pf-up-t text-balance text-[clamp(50px,7.6vw,108px)] font-bold leading-none tracking-[-0.04em] text-foreground"
          style={delay(0.08)}
        >
          {t("home.hero.title")}
        </h1>
        <p
          className="pf-up mx-auto mt-6 max-w-[620px] text-[clamp(18px,1.6vw,21px)] leading-[1.45] text-pf-ink2"
          style={delay(0.16)}
        >
          {t("home.hero.subtitle")}
        </p>
        <div className="pf-up mt-9 flex flex-wrap items-center justify-center gap-3.5" style={delay(0.24)}>
          <AppStoreBadges variant="pill" align="center" location="hero" utmCampaign="home_clarity" />
          <QrCard />
        </div>
        <p className="pf-up mt-[18px] text-sm tabular-nums text-pf-ink2" style={delay(0.32)}>
          {t("home.hero.priceLine")}
        </p>
      </div>

      {/* Phone stage */}
      <div className="relative mx-auto mt-11 max-w-[1200px] px-[22px] pt-6 md:mt-16">
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-10 z-0 aspect-square w-[min(1000px,140vw)] -translate-x-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, hsl(var(--pf-sage) / 0.62), hsl(var(--pf-sage-soft) / 0.35) 55%, hsl(var(--background) / 0) 100%)",
          }}
        />

        {/* Ask AI — behind, start side */}
        <div className="absolute start-1/2 top-14 z-[1] -ms-[210px] w-[170px] -rotate-[7deg] rtl:rotate-[7deg] md:top-[84px] md:-ms-[360px] md:w-[250px] md:-rotate-[8deg] md:rtl:rotate-[8deg]">
          <div className="pf-rise2">
            <div className="pf-float-a">
              <PhoneFrame>
                <img
                  src={askAiShot}
                  alt={t("home.hero.altAskAi")}
                  width={SCREEN_WIDTH}
                  height={SCREEN_HEIGHT}
                  loading="lazy"
                  decoding="async"
                />
              </PhoneFrame>
            </div>
          </div>
        </div>

        {/* Health Check — behind, end side */}
        <div className="absolute start-1/2 top-14 z-[1] ms-[40px] w-[170px] rotate-[7deg] rtl:-rotate-[7deg] md:top-[84px] md:ms-[110px] md:w-[250px] md:rotate-[8deg] md:rtl:-rotate-[8deg]">
          <div className="pf-rise2">
            <div className="pf-float-b">
              <PhoneFrame>
                <img
                  src={healthShot}
                  alt={t("home.hero.altHealth")}
                  width={SCREEN_WIDTH}
                  height={SCREEN_HEIGHT}
                  loading="lazy"
                  decoding="async"
                />
              </PhoneFrame>
            </div>
          </div>
        </div>

        {/* Home — front, centre (LCP candidate: eager, high priority, never transparent) */}
        <div className="pf-rise relative z-[3] mx-auto w-[220px] md:w-[300px]">
          <PhoneFrame size="lg">
            <img
              src={homeShot}
              alt={t("home.hero.altHome")}
              width={SCREEN_WIDTH}
              height={SCREEN_HEIGHT}
              loading="eager"
              {...HIGH_PRIORITY}
              decoding="async"
            />
          </PhoneFrame>
        </div>

        {/* Callouts: absolute with leader lines on wide screens, a chip row below */}
        <ul
          aria-label={t("home.hero.calloutsLabel")}
          className="relative z-[4] mx-auto mt-9 flex max-w-[760px] list-none flex-wrap justify-center gap-2.5 p-0 min-[1100px]:pointer-events-none min-[1100px]:absolute min-[1100px]:inset-0 min-[1100px]:m-0 min-[1100px]:block min-[1100px]:max-w-none"
        >
          {CALLOUTS.map(({ key, side, pos, dot, delay: d }) => (
            <li
              key={key}
              className={`pf-co pointer-events-auto relative min-[1100px]:absolute min-[1100px]:w-[236px] ${pos}`}
              style={{ "--d": `${d}s` } as CSSProperties}
            >
              <div className="rounded-2xl border border-foreground/[0.08] bg-white/95 px-4 py-3 text-start shadow-[0_18px_40px_-22px_hsl(var(--foreground)/0.35)]">
                <span className="flex items-center gap-[7px] text-[11px] font-semibold uppercase tracking-[0.08em] text-pf-ink2">
                  <span aria-hidden="true" className={`h-[7px] w-[7px] rounded-full ${dot}`} />
                  {t(`home.hero.callouts.${key}.label`)}
                </span>
                <span className="mt-1 block text-base font-semibold leading-[1.3] tracking-[-0.01em] text-foreground">
                  {t(`home.hero.callouts.${key}.text`)}
                </span>
              </div>
              <span
                aria-hidden="true"
                className={`pf-lead absolute top-1/2 hidden h-px w-[110px] bg-foreground/35 min-[1100px]:block ${
                  side === "start" ? "start-full origin-left rtl:origin-right" : "end-full origin-right rtl:origin-left"
                }`}
              />
              <span
                aria-hidden="true"
                className={`pf-dot absolute top-1/2 -mt-1 hidden h-[9px] w-[9px] rounded-full border-2 border-foreground bg-white min-[1100px]:block ${
                  side === "start" ? "-ms-1 start-[calc(100%+110px)]" : "-me-1 end-[calc(100%+110px)]"
                }`}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Hero;
